import { computed, onScopeDispose, ref, watch } from 'vue';
import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
import { loadParticipants, loadParticipantsWithChildren } from './EventGuestList.access';
import { buildGuestRows, mergeParticipants } from './build-guest-rows';
import { DEFAULT_PAGE_SIZE, type EventParticipant, type OrderByField, type OrderDirection, type StatusFilter } from './guest-list-types';

type LoadMode = 'reset' | 'more' | 'reloadCurrent';

const SEARCH_DEBOUNCE_MS = 500;

export function useGuestList(eventId: () => string) {
    const status = ref<StatusFilter>('');
    const orderBy = ref<OrderByField>('Created');
    const orderDirection = ref<OrderDirection>('desc');
    const freeText = ref('');

    const participants = ref<EventParticipant[]>([]);
    /** Rows returned by the paged query since the last reset — the skip for the next page. */
    const fetchedCount = ref(0);
    /** Count for the current filter/search. */
    const matchingCount = ref(0);
    /** Count without filter/search, only refreshed by an unfiltered load (legacy totalGuestCount). */
    const totalCount = ref(0);
    const loading = ref(false);
    const childrenLoading = ref(false);
    const hasLoaded = ref(false);
    /** Bumped after a mutation so the status counts reload. */
    const countsVersion = ref(0);

    const selectedIds = ref<string[]>([]);
    const checkboxesToggled = ref(false);

    const rows = computed(() => buildGuestRows(participants.value));
    const searchTerm = computed(() => freeText.value.toLowerCase().trim());
    const allLoaded = computed(() => {
        if (fetchedCount.value >= matchingCount.value) return true;
        // Accompanying guests loaded with their group count towards matchingCount without advancing fetchedCount.
        // A search can't be matched client-side, so it falls back to fetchedCount alone.
        if (searchTerm.value) return false;
        const loaded = status.value ? participants.value.filter((p) => p.StatusName === status.value) : participants.value;
        return loaded.length >= matchingCount.value;
    });
    const showCheckboxes = computed(() => checkboxesToggled.value || selectedIds.value.length > 0);
    const selectedAcceptedCount = computed(
        () => participants.value.filter((p) => p.StatusName === 'Accepted' && selectedIds.value.includes(p.EventParticipantId)).length
    );

    /** Accepted guests matching the search, i.e. what the bulk "Accepted → Attended" change affects. Null without a search. */
    const acceptedMatchingCount = ref<number | null>(null);

    async function loadAcceptedMatchingCount(signal?: AbortSignal) {
        const term = searchTerm.value;
        if (!term) {
            acceptedMatchingCount.value = null;
            return;
        }
        const result = await loadParticipants(
            eventId(),
            { top: 1, skip: 0, status: 'Accepted', orderBy: orderBy.value, orderDirection: orderDirection.value, freeText: term },
            signal
        );
        if (!signal?.aborted && term === searchTerm.value) acceptedMatchingCount.value = result.Count;
    }

    let abortController: AbortController | null = null;
    /** Bumped by loads that replace the list. A "load more" only appends, so it doesn't invalidate a group refresh. */
    let listGeneration = 0;

    async function load(mode: LoadMode) {
        abortController?.abort();
        const controller = new AbortController();
        abortController = controller;
        if (mode !== 'more') listGeneration++;

        // reloadCurrent re-fetches everything loaded so far so the user keeps their place after a mutation.
        const top = mode === 'reloadCurrent' ? Math.max(fetchedCount.value, DEFAULT_PAGE_SIZE) : DEFAULT_PAGE_SIZE;
        const skip = mode === 'more' ? fetchedCount.value : 0;
        // Read when needed rather than snapshotted, so groups refreshed while this load is in flight aren't reverted.
        const loadedIds = () => new Set((mode === 'more' ? participants.value : []).map((p) => p.EventParticipantId));

        loading.value = true;
        try {
            const [result] = await Promise.all([
                loadParticipants(
                    eventId(),
                    { top, skip, status: status.value, orderBy: orderBy.value, orderDirection: orderDirection.value, freeText: searchTerm.value },
                    controller.signal
                ),
                mode === 'more' ? undefined : loadAcceptedMatchingCount(controller.signal),
            ]);
            if (controller.signal.aborted) return;

            const alreadyLoaded = loadedIds();
            let incoming = result.Results.filter((p) => !alreadyLoaded.has(p.EventParticipantId));

            const parentIds = incoming
                .filter((p) => p.EventParticipantParentId !== null || p.HasChildren)
                .map((p) => p.EventParticipantParentId ?? p.EventParticipantId);

            if (parentIds.length > 0) {
                childrenLoading.value = true;
                try {
                    const withChildren = await loadParticipantsWithChildren(eventId(), [...new Set(parentIds)], controller.signal);
                    if (controller.signal.aborted) return;
                    incoming = mergeParticipants(incoming, withChildren);
                } catch {
                    if (controller.signal.aborted) return;
                    bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
                } finally {
                    childrenLoading.value = false;
                }
            }

            const stillLoaded = loadedIds();
            participants.value = mergeParticipants(
                mode === 'more' ? participants.value : [],
                incoming.filter((p) => !stillLoaded.has(p.EventParticipantId))
            );
            // Relative for "load more": a refresh meanwhile may have adjusted the offset for rows that left the filter.
            fetchedCount.value = (mode === 'more' ? fetchedCount.value : 0) + result.Results.length;
            matchingCount.value = result.Count;
            if (!status.value && !searchTerm.value && skip === 0) totalCount.value = result.Count;
            hasLoaded.value = true;
        } catch {
            if (controller.signal.aborted) return;
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
        } finally {
            if (abortController === controller) loading.value = false;
        }
    }

    const reset = () => load('reset');
    const loadMore = () => load('more');
    const reloadCurrent = () => load(hasLoaded.value ? 'reloadCurrent' : 'reset');
    /** Reloads after a mutation. Unlike the legacy reload on every fetch, the status counts only reload when data changed. */
    const reloadAfterChange = () => {
        countsVersion.value++;
        return reloadCurrent();
    };

    const groupId = (p: EventParticipant) => p.EventParticipantParentId ?? p.EventParticipantId;

    /**
     * Re-fetches only the groups (primary guest + accompanying guests) of the given participants and replaces them in
     * place, instead of reloading every loaded row after a single-row mutation. Rows keep their position.
     */
    async function refreshGroups(eventParticipantIds: string[]) {
        const ids = new Set(eventParticipantIds);
        const groupIds = new Set(participants.value.filter((p) => ids.has(p.EventParticipantId)).map(groupId));
        if (groupIds.size === 0) return;

        const startedWith = listGeneration;
        let fresh: EventParticipant[];
        try {
            [fresh] = await Promise.all([loadParticipantsWithChildren(eventId(), [...groupIds]), loadAcceptedMatchingCount()]);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
            return;
        }
        // A list-replacing load started meanwhile brings fresh data itself, possibly for another filter.
        if (listGeneration !== startedWith) return;

        // A reload only fetches childless groups through the paged list, which leaves EventParticipantData empty for primary
        // guests. Match that, so a refresh doesn't add the "Primary guest" label to a row that a reload would show without it.
        fresh = fresh.map((p) => {
            if (!p.ContactId || p.EventParticipantParentId) return p;
            const hasChildren = fresh.some((c) => c.EventParticipantParentId === p.EventParticipantId);
            return hasChildren ? p : { ...p, EventParticipantData: null };
        });

        const freshIds = new Set(fresh.map((p) => p.EventParticipantId));
        const previous = participants.value.filter((p) => groupIds.has(groupId(p)));
        // Accompanying guests removed server-side (e.g. by an edited survey answer) drop out of their group.
        let next = mergeParticipants(
            participants.value.filter((p) => !groupIds.has(groupId(p)) || freshIds.has(p.EventParticipantId)),
            fresh
        );

        // Like a reload, a group stays listed under a status filter only while one of its members matches it.
        const filter = status.value;
        if (filter) {
            const leftFilter = new Set([...groupIds].filter((id) => !fresh.some((p) => groupId(p) === id && p.StatusName === filter)));
            if (leftFilter.size > 0) {
                next = next.filter((p) => !leftFilter.has(groupId(p)));
                // Paged rows that left the filter shift the server-side offset. Over-counting only re-fetches rows
                // that mergeParticipants de-duplicates, while under-counting would skip rows.
                const removedMatching = previous.filter((p) => leftFilter.has(groupId(p)) && p.StatusName === filter).length;
                fetchedCount.value = Math.max(0, fetchedCount.value - removedMatching);
                matchingCount.value = Math.max(0, matchingCount.value - removedMatching);
                const remaining = new Set(next.map((p) => p.EventParticipantId));
                selectedIds.value = selectedIds.value.filter((id) => remaining.has(id));
            }
        }

        participants.value = next;
        countsVersion.value++;
    }

    /** Re-fetches the groups of every loaded participant linked to the contact. */
    const refreshContact = (contactId: string) =>
        refreshGroups(participants.value.filter((p) => p.ContactId === contactId).map((p) => p.EventParticipantId));

    // Cancelled guests are listed by Modified desc; the previous order is restored when leaving the tab.
    let orderBeforeCancelled: { field: OrderByField; direction: OrderDirection } | null = null;

    function setStatus(newStatus: StatusFilter) {
        const previous = status.value;

        if (newStatus === 'Cancelled') {
            orderBeforeCancelled = { field: orderBy.value, direction: orderDirection.value };
            orderBy.value = 'Modified';
            orderDirection.value = 'desc';
        } else if (previous === 'Cancelled' && orderBeforeCancelled) {
            orderBy.value = orderBeforeCancelled.field;
            orderDirection.value = orderBeforeCancelled.direction;
        }

        status.value = newStatus;
        selectedIds.value = [];
        reset();
    }

    function setOrder(field: OrderByField, direction: OrderDirection) {
        orderBy.value = field;
        orderDirection.value = direction;
        reloadCurrent();
    }

    let searchTimer: number | undefined;
    watch(freeText, () => {
        clearTimeout(searchTimer);
        searchTimer = window.setTimeout(reset, SEARCH_DEBOUNCE_MS);
    });

    onScopeDispose(() => {
        clearTimeout(searchTimer);
        abortController?.abort();
    });

    function toggleSelected(eventParticipantId: string) {
        selectedIds.value = selectedIds.value.includes(eventParticipantId)
            ? selectedIds.value.filter((id) => id !== eventParticipantId)
            : [...selectedIds.value, eventParticipantId];
    }

    function setSelected(eventParticipantIds: string[]) {
        selectedIds.value = eventParticipantIds;
    }

    function clearSelection() {
        selectedIds.value = [];
    }

    return {
        status,
        orderBy,
        orderDirection,
        freeText,
        searchTerm,
        participants,
        rows,
        matchingCount,
        totalCount,
        acceptedMatchingCount,
        loading,
        childrenLoading,
        hasLoaded,
        countsVersion,
        allLoaded,
        selectedIds,
        checkboxesToggled,
        showCheckboxes,
        selectedAcceptedCount,
        reset,
        loadMore,
        reloadAfterChange,
        refreshGroups,
        refreshContact,
        setStatus,
        setOrder,
        toggleSelected,
        setSelected,
        clearSelection,
    };
}

export type GuestListState = ReturnType<typeof useGuestList>;
